<template>
    <div v-if="show" class="modal">
        <div class="modal-content max-w-lg bg-white rounded-md shadow overflow-hidden">
            <span class="close" @click="closeModal">&times;</span>

            <div class="lg:px-8">
                <div class="pb-4 w-full">
                    <h2 class="page-title">Додаткові послуги</h2>
                    <h3 class="page-subtitle mt-2">
                        {{ preOrder?.point?.name ?? '' }} · {{ preOrder?.lake?.name ?? '' }}
                    </h3>
                </div>

                <!-- Loading -->
                <div v-if="loading" class="flex justify-center items-center py-8">
                    <span class="text-gray-500">Завантаження...</span>
                </div>

                <template v-else>
                    <!-- Already purchased -->
                    <div v-if="purchased.length > 0" class="mb-5">
                        <p class="block text-sm font-medium text-gray-700 mb-2">Вже придбано:</p>
                        <div class="space-y-2">
                            <div v-for="attr in purchased" :key="attr.pivot_id"
                                 class="flex items-center justify-between border border-gray-200 rounded px-4 py-2">
                                <div>
                                    <p class="text-sm font-medium text-gray-800">{{ attr.title }}</p>
                                    <p class="text-xs text-gray-500">{{ attr.price }} грн</p>
                                </div>
                                <span
                                    class="text-xs font-semibold"
                                    :class="attr.is_payed ? 'text-green-600' : 'text-amber-500'"
                                >
                                    {{ attr.is_payed ? 'Оплачено' : 'Очікує оплати' }}
                                </span>
                            </div>
                        </div>
                        <div v-if="hasPending" class="mt-3">
                            <loading-button
                                class="btn-indigo btn-booking modal-booking-btn"
                                :loading="retrying"
                                @click.prevent="retryPayment"
                            >
                                Повторити оплату
                            </loading-button>
                        </div>
                    </div>

                    <!-- Available to purchase -->
                    <div v-if="available.length > 0">
                        <p class="block text-sm font-medium text-gray-700 mb-2">Доступні послуги:</p>
                        <div class="space-y-2">
                            <div v-for="attr in available" :key="attr.id"
                                 class="flex items-center justify-between border rounded px-4 py-3"
                                 :class="quantities[attr.id]
                                     ? 'border-indigo-400 bg-indigo-50'
                                     : 'border-gray-200'"
                            >
                                <div class="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        :checked="!!quantities[attr.id]"
                                        class="w-4 h-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
                                        @change="toggleAttr(attr.id, $event.target.checked)"
                                    >
                                    <span class="text-sm text-gray-800">{{ attr.title }}</span>
                                </div>
                                <div class="flex items-center gap-3">
                                    <div v-if="quantities[attr.id]" class="flex items-center gap-1">
                                        <button
                                            type="button"
                                            class="w-6 h-6 rounded border border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600 text-sm leading-none"
                                            @click="changeQty(attr.id, -1)"
                                        >−</button>
                                        <span class="w-6 text-center text-sm font-medium">{{ quantities[attr.id] }}</span>
                                        <button
                                            type="button"
                                            class="w-6 h-6 rounded border border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600 text-sm leading-none"
                                            @click="changeQty(attr.id, 1)"
                                        >+</button>
                                    </div>
                                    <span class="text-sm font-semibold text-indigo-600 min-w-[60px] text-right">
                                        {{ quantities[attr.id] ? attr.price * quantities[attr.id] : attr.price }} грн
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Total & pay button -->
                        <div class="mt-5 border-t border-gray-100 pt-4">
                            <div class="flex items-center justify-between mb-3">
                                <span class="text-sm text-gray-600">Разом:</span>
                                <span class="text-base font-bold text-indigo-700">{{ totalAmount }} грн</span>
                            </div>
                            <div class="modal-booking-btn-wrapper flex flex-col gap-2">
                                <loading-button
                                    class="btn-indigo btn-booking modal-booking-btn"
                                    :loading="processing"
                                    :disabled="selectedCount === 0"
                                    @click.prevent="purchase"
                                >
                                    Оплатити
                                </loading-button>
                                <loading-button
                                    v-if="canPayWithBalance"
                                    class="btn-indigo btn-booking modal-booking-btn"
                                    :loading="processingBalance"
                                    :disabled="selectedCount === 0"
                                    @click.prevent="purchaseWithBalance"
                                >
                                    Сплатити з балансу
                                </loading-button>
                            </div>
                        </div>
                    </div>

                    <!-- Empty -->
                    <div v-if="available.length === 0 && purchased.length === 0" class="py-6 text-center text-gray-400 text-sm">
                        Немає доступних послуг для цього місця
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import LoadingButton from "@/Shared/LoadingButton";
import axios from "axios";
import {route} from "ziggy-js";

export default {
    name: "CabinetAttributeModal",
    components: { LoadingButton },
    props: {
        show: Boolean,
        preOrder: Object,
        user: Object,
    },
    emits: ['update:show'],
    data() {
        return {
            loading:           false,
            processing:        false,
            processingBalance: false,
            retrying:          false,
            available:         [],
            purchased:         [],
            quantities:        {}, // { [attrId]: quantity }
        };
    },
    computed: {
        selectedCount() {
            return Object.keys(this.quantities).length;
        },
        totalAmount() {
            return this.available.reduce((sum, a) => {
                return sum + (this.quantities[a.id] ? a.price * this.quantities[a.id] : 0);
            }, 0);
        },
        hasPending() {
            return this.purchased.some(a => !a.is_payed);
        },
        canPayWithBalance() {
            return !!this.user && Number(this.user.internal_balance) >= this.totalAmount;
        },
    },
    watch: {
        show: {
            immediate: true,
            handler(newVal) {
                if (newVal && this.preOrder) {
                    this.fetchAttributes();
                }
            }
        }
    },
    methods: {
        route,
        closeModal() {
            this.$emit('update:show', false);
            this.quantities = {};
            this.available = [];
            this.purchased = [];
        },
        toggleAttr(attrId, checked) {
            if (checked) {
                this.quantities = { ...this.quantities, [attrId]: 1 };
            } else {
                const { [attrId]: _, ...rest } = this.quantities;
                this.quantities = rest;
            }
        },
        changeQty(attrId, delta) {
            const current = this.quantities[attrId] || 1;
            const next = current + delta;
            if (next < 1) {
                const { [attrId]: _, ...rest } = this.quantities;
                this.quantities = rest;
            } else {
                this.quantities = { ...this.quantities, [attrId]: next };
            }
        },
        async fetchAttributes() {
            this.loading = true;
            try {
                const response = await axios.get(
                    this.route('cabinet.attributes.available', { preOrder: this.preOrder.id })
                );
                this.available = response.data.available || [];
                this.purchased = response.data.purchased || [];
            } catch (e) {
                console.error(e);
            } finally {
                this.loading = false;
            }
        },
        purchase() {
            if (!this.selectedCount) return;
            this.processing = true;
            const attributes = Object.entries(this.quantities).map(([id, quantity]) => ({
                id: parseInt(id),
                quantity,
            }));
            this.$inertia.post(
                this.route('cabinet.attributes.purchase', { preOrder: this.preOrder.id }),
                { attributes },
                { onError: () => { this.processing = false; } }
            );
        },
        purchaseWithBalance() {
            if (!this.selectedCount) return;
            if (!confirm('Оплатити послуги з внутрішнього балансу?')) return;
            this.processingBalance = true;
            const attributes = Object.entries(this.quantities).map(([id, quantity]) => ({
                id: parseInt(id),
                quantity,
            }));
            this.$inertia.post(
                this.route('cabinet.attributes.purchase-balance', { preOrder: this.preOrder.id }),
                { attributes },
                { onError: () => { this.processingBalance = false; } }
            );
        },
        retryPayment() {
            this.retrying = true;
            this.$inertia.post(
                this.route('cabinet.attributes.retry', { preOrder: this.preOrder.id }),
                {},
                { onError: () => { this.retrying = false; } }
            );
        },
    },
};
</script>

<style scoped>
.modal {
    display: block;
    position: fixed;
    z-index: 50;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: auto;
    background-color: rgba(0, 0, 0, 0.4);
}

.modal-content {
    background-color: #fefefe;
    margin: 15% auto;
    padding: 20px;
    border: 1px solid #888;
    width: 80%;
}

.close {
    color: #aaa;
    float: right;
    font-size: 28px;
    font-weight: bold;
}

.close:hover,
.close:focus {
    color: black;
    text-decoration: none;
    cursor: pointer;
}
</style>
